import React from 'react';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Title({
  as,
  level,
  children,
  id,
  size,
  weight = 'semibold',
  align = 'left',
  color = 'default',
  truncate = false,
  noWrap = false,
  underline = false,
  gradient = false,
  gradientFrom = 'from-blue-600',
  gradientTo = 'to-violet-600',
  className,
  style,
  description,
  badge,
  icon,
  actions,
  spacing = 'normal', 
  gutterBottom,

}) {
  const Component = as || 'h2';

  const levelToSize = {
    1: '3xl', // h1
    2: '2xl', // h2
    3: 'xl',  // h3
    4: 'lg',  // h4
    5: 'md',  // h5
    6: 'sm',  // h6
  };
  const inferredSize = size || (level ? levelToSize[level] : undefined);
  const effectiveSize = inferredSize || (Component.startsWith('h') ? levelToSize[Number(Component[1])] : 'lg') || 'lg';

  const SIZE = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
  };

  const WEIGHT = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  };

  const ALIGN = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const COLOR = {
    default: 'text-gray-900',
    muted: 'text-gray-700',
    primary: 'text-blue-700',
    success: 'text-green-700',
    warning: 'text-amber-700',
    danger: 'text-red-700',
  };

  const SPACING = {
    tight: 'mb-1',
    normal: 'mb-2',
    loose: 'mb-3',
  };

  const baseTitleClasses = cn(
    SIZE[effectiveSize],
    WEIGHT[weight],
    ALIGN[align],
    gradient ? 'bg-clip-text text-transparent' : COLOR[color],
    underline === 'always' ? 'underline underline-offset-4' : underline ? 'hover:underline underline-offset-4' : '',
    truncate ? 'truncate' : '',
    noWrap ? 'whitespace-nowrap overflow-hidden text-ellipsis' : '',
  );

  const containerMB =
    typeof gutterBottom === 'number'
      ? `mb-${gutterBottom}`
      : gutterBottom
      ? 'mb-4'
      : '';

  const bottomSpacing = !gutterBottom ? SPACING[spacing] : '';

  // Wrap title with anchor when id exists and underline on hover
  const maybeAnchor = (content) => {
    if (!id) return content;
    return (
      <a href={`#${id}`} className="group focus:outline-none focus-visible:ring-2 ring-offset-2 ring-blue-600">
        <span className="sr-only">Jump to section</span>
        {content}
      </a>
    );
  };

  const TitleRow = (
    <div className={cn('flex items-start gap-2', ALIGN[align] !== 'text-left' ? 'justify-center' : '')}>
      {icon && (
        <span className={cn(
          'mt-[2px] text-gray-400',
          effectiveSize === '3xl' ? 'text-2xl' : effectiveSize === '2xl' ? 'text-xl' : 'text-lg'
        )}>
          {icon}
        </span>
      )}

      <div className={cn('flex-1 min-w-0', ALIGN[align] !== 'text-left' ? 'flex justify-center' : '')}>
        {maybeAnchor(
          <Component
            id={id}
            className={cn(baseTitleClasses, gradient ? `bg-gradient-to-r ${gradientFrom} ${gradientTo}` : '', className)}
            style={style}
          >
            {children}
          </Component>
        )}
        {description && (
          <p
            className={cn(
              'mt-1 text-sm text-gray-600',
              ALIGN[align]
            )}
          >
            {description}
          </p>
        )}
      </div>

      {(badge || actions) && (
        <div className="flex items-center gap-2 shrink-0">
          {badge ? <div className="inline-flex">{badge}</div> : null}
          {actions ? <div className="inline-flex">{actions}</div> : null}
        </div>
      )}
    </div>
  );

  return (
    <div className={cn(containerMB || bottomSpacing)}>
      {TitleRow}
    </div>
  );
}